
import React, { useState, useEffect } from 'react';
import { TrustDocument, TrustVersion, TrustCategory } from '../../types';
import { firebaseService } from '../../services/firebaseService';
// Fix: Added ChevronRight to the lucide-react imports to resolve the missing icon error
import { Shield, Brain, Activity, ArrowLeft, Clock, Info, CheckCircle2, ShieldCheck, Scale, FileText, Lock, BookOpen, ChevronRight } from 'lucide-react';

export const TrustCenter: React.FC = () => {
  const [docs, setDocs] = useState<TrustDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<TrustDocument | null>(null);
  const [activeVersion, setActiveVersion] = useState<TrustVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPublishedDocs = async () => {
      const allDocs = await firebaseService.getTrustDocuments();
      const published = allDocs.filter(d => d.status === 'PUBLISHED');
      setDocs(published);
      setIsLoading(false);
    };
    loadPublishedDocs();
  }, []);

  const handleSelectDoc = async (docObj: TrustDocument) => {
    setIsLoading(true);
    try {
      const versions = await firebaseService.getTrustVersions(docObj.id);
      const published = versions.find(v => v.versionId === docObj.publishedVersionId);
      if (published) {
        setSelectedDoc(docObj);
        setActiveVersion(published);
      } else {
        alert("Published shard not resolved. Linking to CMD...");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryIcon = (category: TrustCategory) => {
    switch(category) {
      case 'PRIVACY': return <Lock size={24} />;
      case 'SECURITY': return <Shield size={24} />;
      case 'LEGAL': return <Scale size={24} />;
      case 'COMPLIANCE': return <Activity size={24} />;
      default: return <FileText size={24} />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-40 gap-4 opacity-50">
        <Activity className="animate-spin text-blue-600" size={32} />
        <p className="text-[10px] font-black uppercase tracking-widest">Retrieving Secure Shards...</p>
      </div>
    );
  }

  if (selectedDoc && activeVersion) {
    return (
      <div className="max-w-3xl mx-auto space-y-12 animate-in fade-in duration-500 pb-20">
        <button 
          onClick={() => { setSelectedDoc(null); setActiveVersion(null); }}
          className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-vanguard-muted hover:text-vanguard-text transition-all"
        >
          <ArrowLeft size={16} /> Return to Trust Center
        </button>

        <div className="space-y-6">
          <div className="flex items-center gap-6">
            <div className="p-5 bg-blue-600 text-white rounded-[1.75rem] shadow-xl shadow-blue-600/20">
              {getCategoryIcon(selectedDoc.category)}
            </div>
            <div>
              <h2 className="text-4xl font-black text-vanguard-text uppercase tracking-tighter leading-none">{selectedDoc.title}</h2>
              <div className="flex items-center gap-4 mt-3">
                 <p className="text-[10px] font-bold text-blue-600 uppercase tracking-[0.4em] flex items-center gap-2">
                   <Clock size={12}/> Effective: {activeVersion.effectiveDate ? new Date(activeVersion.effectiveDate).toLocaleDateString() : 'N/A'}
                 </p>
                 <span className="w-1 h-1 bg-vanguard-border rounded-full" />
                 <p className="text-[10px] font-bold text-vanguard-muted uppercase tracking-[0.4em]">
                   V.{activeVersion.versionNumber}
                 </p>
              </div>
            </div>
          </div>

          <div className="glass p-12 rounded-[3.5rem] border border-vanguard-border shadow-2xl">
            <div className="prose prose-vanguard max-w-none prose-headings:uppercase prose-headings:tracking-tight prose-headings:font-black prose-p:text-vanguard-muted prose-p:leading-relaxed dark:prose-invert">
              {activeVersion.content.split('\n').map((line, i) => {
                if (line.startsWith('# ')) return <h1 key={i} className="text-3xl mb-6">{line.replace('# ', '')}</h1>;
                if (line.startsWith('## ')) return <h2 key={i} className="text-2xl mt-10 mb-4">{line.replace('## ', '')}</h2>;
                if (line.startsWith('### ')) return <h3 key={i} className="text-xl mt-8 mb-3">{line.replace('### ', '')}</h3>;
                if (line.startsWith('* ')) return <li key={i} className="ml-4 mb-1 text-vanguard-muted">{line.replace('* ', '')}</li>;
                return line.trim() === '' ? <br key={i} /> : <p key={i} className="mb-4">{line}</p>;
              })}
            </div>
          </div>
        </div>

        <div className="p-10 bg-vanguard-text text-vanguard-bg rounded-[2.5rem] flex items-center gap-6 shadow-2xl">
           <ShieldCheck size={32} />
           <div className="space-y-1">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-60">Integrity Verified</p>
              <p className="text-xs font-bold uppercase tracking-widest">This protocol shard was last secured on {new Date(selectedDoc.lastPublishedAt || Date.now()).toLocaleString()} via Command Shard.</p>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-12 pb-20 animate-in fade-in duration-500">
      <div className="space-y-2 border-b border-vanguard-border pb-10">
        <h2 className="text-6xl font-black text-vanguard-text tracking-tighter uppercase leading-none">Trust Center</h2>
        <p className="text-[10px] font-bold text-vanguard-muted uppercase tracking-[0.5em]">Governance, Security & Transparency Protocols</p>
      </div>

      {docs.length === 0 ? (
        <div className="py-20 text-center space-y-6 opacity-30">
           <BookOpen size={64} className="mx-auto" />
           <p className="text-sm font-black uppercase tracking-[0.4em]">No published protocols at this time.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {docs.map(doc => (
            <div 
              key={doc.id}
              onClick={() => handleSelectDoc(doc)}
              className="glass p-10 rounded-[3rem] border border-vanguard-border shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer group flex flex-col justify-between h-full"
            >
              <div className="space-y-6">
                <div className="p-5 rounded-3xl bg-blue-600/10 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all w-fit shadow-sm">
                  {getCategoryIcon(doc.category)}
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-vanguard-text uppercase tracking-tighter leading-tight">{doc.title}</h3>
                  <div className="flex items-center gap-2">
                     <span className="px-2 py-0.5 rounded-md bg-black/5 dark:bg-white/5 text-[8px] font-black uppercase tracking-widest text-vanguard-muted">{doc.category}</span>
                     <span className="text-[8px] font-bold text-vanguard-muted uppercase tracking-widest">Shard V.{doc.currentVersionId ? '1.4' : '1.0'}</span>
                  </div>
                </div>
              </div>
              <div className="mt-10 flex items-center justify-between pt-6 border-t border-vanguard-border">
                <span className="text-[10px] font-black uppercase text-blue-600 tracking-widest">Execute Protocol</span>
                <ChevronRight className="text-vanguard-muted opacity-20 group-hover:opacity-100 group-hover:translate-x-1 transition-all" size={20} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="p-16 glass rounded-[3.5rem] bg-gradient-to-br from-indigo-900/10 to-transparent border border-vanguard-border flex flex-col items-center text-center space-y-8">
         <ShieldCheck size={56} className="text-blue-600" />
         <div className="space-y-3">
            <h4 className="text-3xl font-black text-vanguard-text uppercase tracking-tight">Vanguard Trust Seal</h4>
            <p className="text-base text-vanguard-muted font-medium max-w-2xl mx-auto leading-relaxed">
              Our infrastructure is audited daily for integrity. Priori Vanguard ensures user data sovereignty and strategic transparency in every deployment cycle. All shards are versioned and immutable.
            </p>
         </div>
         <div className="pt-4 flex gap-4">
            <div className="px-5 py-2 bg-emerald-500/10 text-emerald-600 rounded-full text-[9px] font-black uppercase tracking-widest border border-emerald-500/20 flex items-center gap-2">
               <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Cluster Integrity Pass
            </div>
            <div className="px-5 py-2 bg-blue-500/10 text-blue-600 rounded-full text-[9px] font-black uppercase tracking-widest border border-blue-500/20 flex items-center gap-2">
               <Shield size={12}/> Encryption: 256-Bit TLS
            </div>
         </div>
      </div>
    </div>
  );
};
